/**
 * The Menu's pages and their icons, in a module of their own so the bottom bar
 * (MobileNav, mounted in the ROOT layout) can read them without pulling the
 * whole drawer into every page's bundle. MobileMenu re-exports both.
 *
 * The Liquid Glass bar (variant B) shows the current page's icon in the circle
 * the tabs fold into; reading it from here means a page cannot look one way in
 * the Menu and another in the bar.
 */
import {
  BarChart3,
  BellRing,
  Briefcase,
  CalendarCheck,
  ClipboardList,
  Clock,
  FileText,
  Flame,
  Gauge,
  Gift,
  Headset,
  Inbox as InboxIcon,
  ListChecks,
  Mail,
  MapPin,
  Repeat,
  Scale,
  Settings as SettingsIcon,
  ShieldCheck,
  Star,
  Package,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { PARTS_NAV_PERMISSION } from '@/lib/skills/nav';

/** The Menu's fixed rows (everything that is not a skill), by path. */
export const MENU_ROW_ICONS = {
  '/actions': InboxIcon,
  '/inbox': Mail,
  '/jobs': Clock,
  '/notepad': FileText,
  '/properties': MapPin,
  '/engineers': Users,
  '/settings': SettingsIcon,
  '/settings/referrals/referrals': Gift,
  '/admin': ShieldCheck,
} as const satisfies Record<string, LucideIcon>;

export interface SkillItem {
  slug: string;
  label: string;
  href: string;
  icon: LucideIcon;
  /**
   * REQUIRED permission gate — the entry is hidden unless
   * `userPermissions[permission]` is true. Mandatory for the same reason it is
   * mandatory on SkillNavItem: seven entries here had none until 2026-08-14,
   * so a subcontractor's Menu listed Quotes, Invoicing, Revenue, Work Orders,
   * Projects, Reviews and Live Map. Keep this list in step with
   * SKILL_NAV_LIST — `skill-nav-permissions.test.ts` asserts they agree.
   */
  permission: string | readonly string[];
  /**
   * Listed only on a phone in Liquid Glass. A row Tom accepted for the glass
   * Menu (docs/glass-batch-4.md) that variant A has never had; variant A's
   * Menu stays exactly as it was.
   */
  glassOnly?: true;
  /**
   * The skill whose switch lists this row, when the row is a page OF a skill
   * rather than a skill of its own. Defaults to `slug`. CIS taken off you is a
   * report under Revenue: `view_revenue_reports` is that skill's key, so the
   * row follows the skill as the key does.
   */
  skill?: string;
  /**
   * Listed only for an account that works as a subcontractor
   * (`SidebarData.doesSubbieWork`). CIS taken off you means nothing to a firm
   * nobody deducts CIS from (Tom, 16 Oct).
   */
  subbieOnly?: true;
}

/**
 * Mirrors SKILL_NAV_LIST + CATEGORY_ORDER in `src/lib/skills/nav.ts` but uses
 * lucide icons matching the rest of the mobile design system.
 *
 * EXPORTED purely so `skill-nav-permissions.test.ts` can assert the two lists
 * agree on slug → permission. Hand-mirrored lists drift — the Service Reminders
 * entry was missing here for a month after the skill shipped — and when the
 * thing that drifts is the permission gate, the drift is a data leak on one
 * platform only, which is the hardest kind to notice.
 */
export const SKILLS_BY_CATEGORY: Record<string, SkillItem[]> = {
  Trade: [
    { slug: 'work-orders', label: 'Work Orders', href: '/work-orders', icon: ClipboardList, permission: 'view_work_orders' },
    { slug: 'gas-certificates', label: 'Certificates', href: '/gas-certs', icon: Flame, permission: 'view_gas_certs' },
    {
      slug: 'gas-calculators',
      label: 'Calculators',
      href: '/gas-calculators',
      icon: Gauge,
      permission: 'use_gas_calculators',
    },
    {
      // 🚨 `use_custom_forms`, the ENGINEER's key, and it changed on 4 September
      // when the page did. It used to be the builder's key with the comment
      // "this is the builder, not the filling-in" — which was true of the old
      // page, because the old page listed TEMPLATES. It lists completed forms
      // now, the way /gas-certs lists issued certificates, and authoring moved
      // behind a Settings button gated on `manage_form_templates` inside it.
      // So the nav gate is "may you have anything to do with forms" and the
      // narrower key guards the one control that writes a template.
      slug: 'form-builder',
      label: 'Forms',
      href: '/forms',
      icon: ListChecks,
      permission: 'use_custom_forms',
    },
    // Parts — both halves of Parts & Van Stock; van stock is a tab of it.
    // Same keys as the desktop row (PARTS_NAV_PERMISSION).
    { slug: 'parts-tracker', label: 'Parts', href: '/parts', icon: Package, permission: PARTS_NAV_PERMISSION },
  ],
  Operations: [
    { slug: 'live-map', label: 'Live Map', href: '/map', icon: MapPin, permission: 'view_live_map' },
    { slug: 'projects', label: 'Projects', href: '/projects', icon: Briefcase, permission: 'view_projects' },
    { slug: 'reviews', label: 'Reviews', href: '/reviews', icon: Star, permission: 'view_reviews' },
    {
      // Was missing here since the skill shipped (2026-07-05), so mobile users
      // could not reach /service-reminders from the menu at all.
      slug: 'service-reminders',
      label: 'Service Reminders',
      href: '/service-reminders',
      icon: BellRing,
      permission: 'view_service_reminders',
    },
    {
      slug: 'on-time-tracker',
      label: 'On Time Tracker',
      href: '/on-time-tracker',
      icon: Clock,
      permission: 'use_on_time_tracker',
    },
    {
      slug: 'voice-receptionist',
      label: 'Receptionist',
      href: '/voice-receptionist',
      icon: Headset,
      permission: 'manage_voice_receptionist',
    },
    {
      // Not in variant A's Menu, where the page was reachable only from the
      // desktop's sidebar, Skills or a card (docs/glass-batch-4.md).
      slug: 'online-booking',
      label: 'Online booking',
      href: '/online-booking',
      icon: CalendarCheck,
      permission: 'manage_online_booking',
      glassOnly: true,
    },
    {
      slug: 'timesheets',
      label: 'Timesheets',
      href: '/timesheets',
      icon: Clock,
      // Either hours key — see SKILL_NAV_LIST's timesheets entry.
      permission: ['view_all_hours', 'view_own_hours'],
    },
  ],
  Finance: [
    { slug: 'quotes', label: 'Quotes', href: '/quotes', icon: FileText, permission: 'view_quotes' },
    { slug: 'invoicing', label: 'Invoicing', href: '/invoices', icon: FileText, permission: 'view_invoices' },
    { slug: 'revenue-reports', label: 'Revenue', href: '/revenue-reports', icon: BarChart3, permission: 'view_revenue_reports' },
    {
      slug: 'service-plans',
      label: 'Service Plans',
      href: '/service-plans',
      icon: Repeat,
      permission: 'view_service_plans',
    },
    {
      // Not in variant A's Menu, where the page had no link anywhere in the app
      // (docs/glass-batch-4.md). Tom's name for what HMRC calls CIS suffered:
      // "CIS" alone clashes with the CIS taken off your OWN subcontractors.
      // Same key as the page (`reports/cis-suffered/page.tsx`).
      slug: 'cis-taken-off-you',
      skill: 'revenue-reports',
      label: 'CIS taken off you',
      href: '/reports/cis-suffered',
      icon: Scale,
      permission: 'view_revenue_reports',
      glassOnly: true,
      subbieOnly: true,
    },
  ],
};

